// Fogos de artifício do Réveillon num canvas transparente: foguetes que sobem com rastro, explodem em peônia,
// anel, palmeira ou chuva de ouro, com estalinhos e brilho somado (modo "lighter"). Paleta dourada, champanhe,
// esmeralda (a cor da marca) e branco.
//
//   createFireworks(canvas, {auto, rate}) -> {launch, salvo, resize, destroy}
//     auto: dispara sozinho (rate = foguetes por segundo, em média)
//   showFireworks({duration}) -> espetáculo na tela inteira, por cima de tudo e sem receber clique; o canvas some
//     sozinho quando a última faísca apaga. Usado no brinde do cartão da home e na virada do ano.
import {prefersReducedMotion} from './season';

const PALETTES = [
    ['#ffe6a3', '#f6c453', '#e2a93b'],
    ['#fff4cf', '#f3d68a', '#ffffff'],
    ['#9ff5dd', '#2fd3a7', '#0fa37f'],
    ['#ffd1c1', '#f1b8a0', '#f6c453'],
    ['#ffffff', '#e6edf5', '#f3d68a'],
];
const SHAPES = ['peony', 'peony', 'ring', 'palm', 'willow'];

const rand = (min, max) => min + Math.random() * (max - min);
const pick = list => list[Math.floor(Math.random() * list.length)];

export function createFireworks(canvas, {auto = false, rate = 0.6} = {}) {
    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let rockets = [];
    let sparks = [];
    let frame = 0;
    let last = performance.now();
    let nextAuto = 0.4;
    let running = false;
    let destroyed = false;
    let onIdle = null;

    function resize() {
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        width = canvas.clientWidth;
        height = canvas.clientHeight;
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    function launch(x = rand(width * 0.15, width * 0.85), peak = rand(height * 0.12, height * 0.42)) {
        if (destroyed) return;
        const startY = height + 8;
        // Velocidade para chegar ao alto pedido com a gravidade do foguete
        const gravity = 480;
        const vy = -Math.sqrt(2 * gravity * Math.max(40, startY - peak));
        rockets.push({x: x + rand(-30, 30), y: startY, vx: rand(-18, 18), vy, gravity, palette: pick(PALETTES), shape: pick(SHAPES)});
        start();
    }

    function salvo(count = 5, spread = 900) {
        for (let i = 0; i < count; i++) {
            setTimeout(() => launch(width * (0.12 + (0.76 * (i + 0.5)) / count)), Math.random() * spread);
        }
    }

    function explode(rocket) {
        const {x, y, palette, shape} = rocket;
        const count = shape === 'ring' ? 46 : shape === 'palm' ? 22 : 70;
        const power = Math.min(width, height) / 520 + 0.6;
        for (let i = 0; i < count; i++) {
            const angle = (i / count) * Math.PI * 2 + rand(-0.05, 0.05);
            let speed;
            if (shape === 'ring') speed = 150 * power;
            else if (shape === 'palm') speed = rand(110, 170) * power;
            else speed = rand(40, 175) * power * Math.sqrt(Math.random() + 0.2);
            const willow = shape === 'willow';
            sparks.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - (shape === 'palm' ? 30 : 0),
                life: willow ? rand(2.2, 3) : rand(1.1, 1.8),
                age: 0,
                drag: willow ? 1.9 : shape === 'palm' ? 1.1 : 1.5,
                gravity: willow ? 55 : 90,
                color: willow ? '#f6c453' : pick(palette),
                size: shape === 'palm' ? 2.4 : rand(1.4, 2.2),
                // Estalinho: pisca no fim da vida
                crackle: Math.random() > 0.72,
            });
        }
        // Clarão no centro
        sparks.push({x, y, vx: 0, vy: 0, life: 0.28, age: 0, drag: 0, gravity: 0, color: '#fff7dd', size: 26, flash: true});
    }

    function step(dt) {
        // Rastro: apaga o quadro anterior aos poucos (fundo transparente)
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = `rgba(0, 0, 0, ${Math.min(1, dt * 14)})`;
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'lighter';

        rockets = rockets.filter(rocket => {
            rocket.vy += rocket.gravity * dt;
            rocket.x += rocket.vx * dt;
            rocket.y += rocket.vy * dt;
            ctx.globalAlpha = 1;
            ctx.fillStyle = '#ffe9b0';
            ctx.beginPath();
            ctx.arc(rocket.x, rocket.y, 1.8, 0, Math.PI * 2);
            ctx.fill();
            // Faísca de rastro do foguete
            if (Math.random() < 0.7) {
                sparks.push({x: rocket.x, y: rocket.y + 2, vx: rand(-12, 12), vy: rand(10, 40), life: rand(0.3, 0.6), age: 0,
                    drag: 2, gravity: 40, color: '#f3d68a', size: 1.1});
            }
            if (rocket.vy >= -12) {
                explode(rocket);
                return false;
            }
            return true;
        });

        sparks = sparks.filter(spark => {
            spark.age += dt;
            if (spark.age >= spark.life) return false;
            const fade = 1 - spark.age / spark.life;
            if (spark.flash) {
                const gradient = ctx.createRadialGradient(spark.x, spark.y, 0, spark.x, spark.y, spark.size);
                gradient.addColorStop(0, `rgba(255, 247, 221, ${0.55 * fade})`);
                gradient.addColorStop(1, 'rgba(255, 247, 221, 0)');
                ctx.globalAlpha = 1;
                ctx.fillStyle = gradient;
                ctx.fillRect(spark.x - spark.size, spark.y - spark.size, spark.size * 2, spark.size * 2);
                return true;
            }
            const drag = Math.exp(-spark.drag * dt);
            spark.vx *= drag;
            spark.vy = spark.vy * drag + spark.gravity * dt;
            spark.x += spark.vx * dt;
            spark.y += spark.vy * dt;
            let alpha = fade;
            if (spark.crackle && fade < 0.45) alpha *= Math.random() > 0.5 ? 1.4 : 0.15;
            ctx.globalAlpha = Math.min(1, alpha);
            ctx.fillStyle = spark.color;
            ctx.beginPath();
            ctx.arc(spark.x, spark.y, spark.size * (0.5 + fade * 0.5), 0, Math.PI * 2);
            ctx.fill();
            return true;
        });
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
    }

    function tick(now) {
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        if (auto && !document.hidden) {
            nextAuto -= dt;
            if (nextAuto <= 0) {
                launch();
                if (Math.random() < 0.18) setTimeout(() => launch(), 260);
                nextAuto = rand(0.6, 1.8) / rate;
            }
        }
        step(dt);
        if (!auto && !rockets.length && !sparks.length) {
            running = false;
            ctx.clearRect(0, 0, width, height);
            if (onIdle) onIdle();
            return;
        }
        frame = requestAnimationFrame(tick);
    }

    function start() {
        if (running || destroyed) return;
        running = true;
        last = performance.now();
        frame = requestAnimationFrame(tick);
    }

    function onVisibility() {
        if (document.hidden) {
            cancelAnimationFrame(frame);
            running = false;
        } else if (auto || rockets.length || sparks.length) {
            start();
        }
    }

    resize();
    document.addEventListener('visibilitychange', onVisibility);
    if (auto) start();

    return {
        launch,
        salvo,
        resize,
        setAuto(value) {
            auto = value;
            if (auto) start();
        },
        whenIdle(callback) {
            onIdle = callback;
            // Parado (aba oculta) e sem nenhuma faísca: já está ocioso
            if (!running && !rockets.length && !sparks.length) callback();
            else start();
        },
        destroy() {
            destroyed = true;
            cancelAnimationFrame(frame);
            document.removeEventListener('visibilitychange', onVisibility);
        },
    };
}

let currentShow = null;

export function showFireworks({duration = 6500} = {}) {
    if (typeof document === 'undefined' || prefersReducedMotion()) return;
    // Já tem espetáculo na tela: só reforça com mais uma salva
    if (currentShow) {
        currentShow.engine.salvo(4);
        return;
    }
    const canvas = document.createElement('canvas');
    canvas.className = 'season-fireworks-show';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    // "Apaga as luzes": um véu azul-noite entra devagar para os fogos brilharem também no tema claro
    requestAnimationFrame(() => canvas.classList.add('is-visible'));
    const engine = createFireworks(canvas, {auto: true, rate: 1.6});
    engine.salvo(5, 600);
    const onResize = () => engine.resize();
    window.addEventListener('resize', onResize);
    currentShow = {engine};
    setTimeout(() => {
        engine.setAuto(false);
        // O véu sai junto com as últimas faíscas
        setTimeout(() => canvas.classList.remove('is-visible'), 1200);
        engine.whenIdle(() => {
            engine.destroy();
            window.removeEventListener('resize', onResize);
            canvas.remove();
            currentShow = null;
        });
    }, duration);
}
